import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * One-click "Añadir" buttons for cheap extras inside the cart drawer.
 *
 * Adds the variant with /cart/add.js, requesting the rendered cart sections, and
 * dispatches a CartLinesUpdateEvent whose promise resolves with those sections.
 * `cart-items-component` morphs the drawer from them (so the free-shipping bar and
 * this list update), and the header cart bubble updates from the same event.
 *
 * @typedef {object} CartUpsellRefs
 * @property {HTMLElement} [error] - Inline error message.
 * @extends {Component<CartUpsellRefs>}
 */
class CartUpsellComponent extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * @param {SubmitEvent} event
   */
  handleAdd = async (event) => {
    event.preventDefault();
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const button = form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) {
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
    }
    this.#hideError();

    const formData = new FormData(form);
    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    }
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const variantId = /** @type {string} */ (formData.get('id'));
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript', { body: formData }),
        signal: this.#controller.signal,
      });
      const data = await response.json();

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { signal: this.#controller.signal });
      const ajaxCart = await cartResponse.json();

      if (data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'cart-upsell-component', itemCount: 1 },
        });
        this.#showError(data.description || data.message || '');
        this.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: false,
          items: ajaxCart.items,
          source: 'cart-upsell-component',
          itemCount: 1,
          sections: data.sections,
        },
      });
    } catch (error) {
      deferred.reject(error);
      if (error?.name === 'AbortError') return;
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
    } finally {
      if (button instanceof HTMLButtonElement && button.isConnected) button.removeAttribute('aria-busy');
    }
  };

  /** @param {string} message */
  #showError(message) {
    const { error } = this.refs;
    if (!error || !message) return;
    error.textContent = message;
    error.classList.remove('hidden');
  }

  #hideError() {
    this.refs.error?.classList.add('hidden');
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
